import React, { useEffect } from 'react';
import {
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  RadioGroup,
  Radio,
  Checkbox,
  FormErrorMessage,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import DateField from 'components/fields/DateField';
import { useAppSelector, useAppDispatch } from 'hooks';
import getDateFromString from 'helpers/getDateFromString';
import getDateString from 'helpers/getDateString';
import {
  setClientFirstName,
  setClientLastName,
  setClientDateOfBirth,
  setClientPhoneNumber,
  setClientEmail,
  setClientGender,
  setClientType,
  setReferral,
  addMissingMandatoryField,
  removeMissingMandatoryField,
} from 'reducers/lifeInsuranceFormReducer';

interface LifeClientInformationProps {
  isLocked?: boolean;
}

const LifeClientInformation = ({
  isLocked = false,
}: LifeClientInformationProps) => {
  const dispatch = useAppDispatch();

  const firstName = useAppSelector(
    (state) => state.lifeInsuranceForm.data.first_name,
  );
  const lastName = useAppSelector(
    (state) => state.lifeInsuranceForm.data.last_name,
  );
  const dateOfBirth = useAppSelector(
    (state) => state.lifeInsuranceForm.data.date_of_birth,
  );
  const phoneNumber = useAppSelector(
    (state) => state.lifeInsuranceForm.data.phone_number,
  );
  const email = useAppSelector(
    (state) => state.lifeInsuranceForm.data.email,
  );
  const gender = useAppSelector(
    (state) => state.lifeInsuranceForm.data.gender,
  );
  const clientType = useAppSelector(
    (state) => state.lifeInsuranceForm.data.new_client_or_existing,
  );
  const referral = useAppSelector(
    (state) => state.lifeInsuranceForm.data.referral,
  );
  const validationErrors = useAppSelector(
    (state) => state.lifeInsuranceForm.validationErrors,
  );

  // Track missing mandatory fields
  useEffect(() => {
    const isComplete = firstName && lastName && dateOfBirth;
    if (!isComplete) {
      dispatch(
        addMissingMandatoryField({
          name: 'sectionLifeClientInformation',
          section: 'Client Information',
        }),
      );
    } else {
      dispatch(removeMissingMandatoryField('sectionLifeClientInformation'));
    }
  }, [firstName, lastName, dateOfBirth, dispatch]);

  return (
    <Card mb="20px" id="sectionLifeClientInformation">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        Client Information
      </Text>

      <VStack spacing={4} align="stretch">
        <HStack spacing={4} align="stretch">
          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.client_information?.first_name}
          >
            <FormLabel ml="0">First Name</FormLabel>
            <Input
              variant="main"
              value={firstName}
              onChange={(e) => dispatch(setClientFirstName(e.target.value))}
              placeholder="Enter first name"
              isDisabled={isLocked}
              borderColor={!firstName ? 'red' : 'inherit'}
            />
            {validationErrors?.first_name && (
              <FormErrorMessage>
                {validationErrors.first_name}
              </FormErrorMessage>
            )}
          </FormControl>

          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.client_information?.last_name}
          >
            <FormLabel ml="0">Last Name</FormLabel>
            <Input
              variant="main"
              value={lastName}
              onChange={(e) => dispatch(setClientLastName(e.target.value))}
              placeholder="Enter last name"
              isDisabled={isLocked}
              borderColor={!lastName ? 'red' : 'inherit'}
            />
            {validationErrors?.last_name && (
              <FormErrorMessage>
                {validationErrors.last_name}
              </FormErrorMessage>
            )}
          </FormControl>
        </HStack>

        <HStack spacing={4} align="stretch">
          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.date_of_birth}
          >
            <DateField
              label="Date of Birth"
              date={getDateFromString(dateOfBirth || '')}
              onChange={(date: Date | null) => {
                dispatch(setClientDateOfBirth(date ? getDateString(date) : ''));
              }}
              required
              isDisabled={isLocked}
            />
            {validationErrors?.date_of_birth && (
              <FormErrorMessage>
                {validationErrors.date_of_birth}
              </FormErrorMessage>
            )}
          </FormControl>

          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.gender}
          >
            <FormLabel ml="0">Gender</FormLabel>
            <Select
              variant="main"
              value={gender}
              onChange={(e) => dispatch(setClientGender(e.target.value))}
              placeholder="Select gender"
              isDisabled={isLocked}
              borderColor={!gender ? 'red' : 'inherit'}
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </Select>
            {validationErrors?.gender && (
              <FormErrorMessage>
                {validationErrors.gender}
              </FormErrorMessage>
            )}
          </FormControl>
        </HStack>

        <HStack spacing={4} align="stretch">
          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.phone_number}
          >
            <FormLabel ml="0">Phone Number</FormLabel>
            <Input
              variant="main"
              type="tel"
              value={phoneNumber || ''}
              onChange={(e) => dispatch(setClientPhoneNumber(e.target.value))}
              placeholder="Enter phone number"
              isDisabled={isLocked}
              borderColor={!phoneNumber ? 'red' : 'inherit'}
            />
            {validationErrors?.phone_number && (
              <FormErrorMessage>
                {validationErrors.phone_number}
              </FormErrorMessage>
            )}
          </FormControl>

          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.email}
          >
            <FormLabel ml="0">Email Address</FormLabel>
            <Input
              variant="main"
              type="email"
              value={email || ''}
              onChange={(e) => dispatch(setClientEmail(e.target.value))}
              placeholder="Enter email address"
              isDisabled={isLocked}
              borderColor={!email ? 'red' : 'inherit'}
            />
            {validationErrors?.email && (
              <FormErrorMessage>
                {validationErrors.email}
              </FormErrorMessage>
            )}
          </FormControl>
        </HStack>

        <HStack spacing={4} align="stretch">
          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.new_client_or_existing}
          >
            <FormLabel ml="0">Client Type</FormLabel>
            <RadioGroup
              value={clientType}
              onChange={(value) =>
                dispatch(setClientType(value as 'new' | 'existing'))
              }
              isDisabled={isLocked}
            >
              <HStack spacing={8}>
                <Radio value="new" isDisabled={isLocked}>
                  New Client
                </Radio>
                <Radio value="existing" isDisabled={isLocked}>
                  Existing Client
                </Radio>
              </HStack>
            </RadioGroup>
            {validationErrors?.new_client_or_existing && (
              <FormErrorMessage>
                {validationErrors.new_client_or_existing}
              </FormErrorMessage>
            )}
          </FormControl>
        </HStack>

        <FormControl isRequired>
          <FormLabel ml="0">Referral</FormLabel>
          <Checkbox
            isChecked={referral}
            onChange={(e) => dispatch(setReferral(e.target.checked))}
            isDisabled={isLocked}
          >
            Yes, this is a referral
          </Checkbox>
        </FormControl>
      </VStack>
    </Card>
  );
};

export default LifeClientInformation;
